import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { IoClose } from "react-icons/io5";
import { MdOutlineRoute } from "react-icons/md";

/**
 * The frame of the run pop-ups (Create run, Runs, Add to run), the same as
 * the route sheet's: a sheet from the bottom on phones, a centred panel from
 * `sm`. Only the body scrolls. Esc and Close call `onClose` unless
 * `closeDisabled` (while something saves).
 */
const RunDialog = ({
  open,
  labelId,
  title,
  subtitle,
  icon: Icon = MdOutlineRoute,
  widthClass = "max-w-[760px]",
  closeDisabled = false,
  onClose,
  footer,
  children,
}) => {
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const lockRef = useRef(closeDisabled);
  lockRef.current = closeDisabled;

  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus();
    const onKey = (event) => {
      if (event.key === "Escape" && !lockRef.current) onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[1300] flex items-end justify-center bg-black/40 pt-[10px] sm:items-start sm:p-[14px]">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        className={`flex max-h-[calc(100dvh-10px)] w-full ${widthClass} flex-col overflow-hidden rounded-t-[16px] border-[1px] border-b-0 border-ui-border bg-ui-page font-manrope text-[12px] text-ui-text shadow-ui-md sm:max-h-[calc(100dvh-28px)] sm:rounded-[11px] sm:border-b-[1px]`}
      >
        <header className="flex shrink-0 items-center gap-[11px] border-b-[1px] border-ui-border bg-ui-surface px-[14px] py-[12px]">
          <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[8px] bg-brand-light text-brand">
            <Icon className="h-[18px] w-[18px]" />
          </span>
          <div className="grid min-w-[0px] flex-1 gap-[2px]">
            <h2
              id={labelId}
              className="m-[0px] font-jakarta text-[15px] font-extrabold leading-[1.25] tracking-[-0.01em] text-ui-text"
            >
              {title}
            </h2>
            {subtitle && (
              <span className="truncate font-manrope text-[11px] font-medium leading-[1.35] text-ui-text-muted">
                {subtitle}
              </span>
            )}
          </div>
          <button
            ref={closeRef}
            type="button"
            aria-label="Close"
            onClick={onClose}
            disabled={closeDisabled}
            className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[8px] text-ui-text-subtle hover:bg-ui-surface-sunken hover:text-ui-text disabled:cursor-not-allowed disabled:opacity-40"
          >
            <IoClose className="h-[18px] w-[18px]" />
          </button>
        </header>
        <div className="flex min-h-[0px] flex-1 flex-col gap-[10px] overflow-auto p-[12px] [&>*]:shrink-0">
          {children}
        </div>
        {footer && (
          <footer className="flex shrink-0 flex-wrap items-center justify-end gap-[8px] border-t-[1px] border-ui-border bg-ui-surface px-[14px] pb-[calc(12px+env(safe-area-inset-bottom,0px))] pt-[10px] sm:py-[10px]">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default RunDialog;
